import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import useRefreshToken from '@/hooks/useRefreshToken';
import { useGetCurrent } from '@/hooks/useGetCurrent';
import { useActivationGuard } from '@/hooks/useActivationGuard';
import useAuth from '@/hooks/useAuth';
import { axiosPrivate } from '@/api/axios';
import { COUNTRY_PREFIXES, USER_API_PATH } from '@/lib/constants';
import { signupSchema, type SignupFormData } from './schemas/signup.schema';
import { useSignup } from './hooks/useSignup';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const LEGALS_TC_URL = 'https://mecanica.mx/terminos-y-condiciones';
const LEGALS_PP_URL = 'https://mecanica.mx/politica-de-privacidad';

export default function Signup() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { signup, isSubmitting, error } = useSignup();
  const refresh = useRefreshToken();
  const getCurrent = useGetCurrent();
  const checkActivation = useActivationGuard();
  const { setCurrentUser } = useAuth();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      vendor: searchParams.get('vendor') || '',
      referrer: searchParams.get('referrer') || '',
      acceptedTC: false,
      acceptedPP: false,
    },
  });

  const country = watch('country');

  // Auto-set country prefix when country changes
  useEffect(() => {
    if (country) {
      const match = COUNTRY_PREFIXES.find(c => c.name === country);
      if (match) {
        setValue('countryPrefix', match.value);
      }
    }
  }, [country, setValue]);

  const onSubmit = async (data: SignupFormData) => {
    await signup(data);

    // After signup + auto-login, refresh and load current user
    try {
      await refresh();
      const fullUser = await getCurrent();
      setCurrentUser(fullUser);

      const activation = checkActivation(fullUser);

      if (activation.shouldPatchLastLogin) {
        try {
          await axiosPrivate.patch(`/${USER_API_PATH}/${fullUser.id}`, {
            lastLogin: new Date().toISOString(),
          });
        } catch {
          // Non-critical
        }
      }

      if (activation.redirect) {
        navigate(activation.redirect, { replace: true });
      } else {
        navigate('/shop/reports/eod', { replace: true });
      }
    } catch {
      // If getCurrent fails after signup, redirect to KYC as default
      navigate('/auth/set-up', { replace: true });
    }
  };

  return (
    <div className="container mx-auto px-4 h-full">
      <div className="flex content-center items-center justify-center h-full py-8">
        <div className="w-full lg:w-6/12 px-4">
          <Card variant="legacy" className="mb-6">
            <div className="rounded-t mb-0 px-6 py-6">
              <div className="text-center mb-3">
                <h6 className="text-slate-500 text-sm font-bold">
                  Crear cuenta
                </h6>
              </div>
              <hr className="mt-6 border-b-1 border-slate-300" />
            </div>
            <div className="flex-auto px-4 lg:px-10 py-10 pt-0">
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div>
                  <Label htmlFor="name">Nombre</Label>
                  <Input id="name" {...register('name')} placeholder="Tu nombre" />
                  {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name.message}</p>}
                </div>

                <div>
                  <Label htmlFor="email">Correo electrónico</Label>
                  <Input id="email" type="email" {...register('email')} placeholder="correo@ejemplo.com" />
                  {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
                </div>

                <div>
                  <Label htmlFor="companyName">Nombre de empresa</Label>
                  <Input id="companyName" {...register('companyName')} placeholder="Mi Taller" />
                  {errors.companyName && <p className="text-red-500 text-xs mt-1">{errors.companyName.message}</p>}
                </div>

                <div>
                  <Label htmlFor="country">País</Label>
                  <Select onValueChange={(val) => setValue('country', val)} defaultValue="">
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona un país" />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRY_PREFIXES.map((cp) => (
                        <SelectItem key={`${cp.name}-${cp.value}`} value={cp.name}>
                          {cp.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.country && <p className="text-red-500 text-xs mt-1">{errors.country.message}</p>}
                </div>

                <div>
                  <Label htmlFor="cellphone">Celular</Label>
                  <div className="flex gap-2">
                    <span className="flex items-center px-3 bg-slate-100 rounded text-sm text-slate-600">
                      +{watch('countryPrefix') || '52'}
                    </span>
                    <Input id="cellphone" {...register('cellphone')} placeholder="5512345678" className="flex-1" />
                  </div>
                  {errors.cellphone && <p className="text-red-500 text-xs mt-1">{errors.cellphone.message}</p>}
                </div>

                <div>
                  <Label htmlFor="password">Contraseña</Label>
                  <Input id="password" type="password" {...register('password')} placeholder="Mínimo 6 caracteres" />
                  {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>}
                </div>

                <div>
                  <Label htmlFor="confirmPassword">Confirmar contraseña</Label>
                  <Input id="confirmPassword" type="password" {...register('confirmPassword')} placeholder="Repite tu contraseña" />
                  {errors.confirmPassword && <p className="text-red-500 text-xs mt-1">{errors.confirmPassword.message}</p>}
                </div>

                <div>
                  <Label htmlFor="vendor">Código de referido (opcional)</Label>
                  <Input id="vendor" {...register('vendor')} placeholder="AF-XXXXXX" />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="acceptedTC"
                      checked={watch('acceptedTC')}
                      onCheckedChange={(checked) => setValue('acceptedTC', checked === true)}
                    />
                    <label htmlFor="acceptedTC" className="text-sm text-slate-600">
                      Acepto los{' '}
                      <a href={LEGALS_TC_URL} target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">
                        términos y condiciones
                      </a>
                    </label>
                  </div>
                  {errors.acceptedTC && <p className="text-red-500 text-xs">{errors.acceptedTC.message}</p>}

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="acceptedPP"
                      checked={watch('acceptedPP')}
                      onCheckedChange={(checked) => setValue('acceptedPP', checked === true)}
                    />
                    <label htmlFor="acceptedPP" className="text-sm text-slate-600">
                      Acepto la{' '}
                      <a href={LEGALS_PP_URL} target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">
                        política de privacidad
                      </a>
                    </label>
                  </div>
                  {errors.acceptedPP && <p className="text-red-500 text-xs">{errors.acceptedPP.message}</p>}
                </div>

                {error && (
                  <p className="text-red-500 text-sm text-center">{error}</p>
                )}

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-slate-800 hover:bg-slate-700"
                >
                  {isSubmitting ? 'Creando cuenta...' : 'Crear cuenta'}
                </Button>
              </form>
            </div>
          </Card>
          <div className="text-center mt-4">
            <Link to="/auth/login" className="text-slate-200">
              <small>Ya tienes cuenta? Inicia sesión</small>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
